/**
 * File upload API layer
 *
 */

import { type MutationOptions } from '@tanstack/react-query';
import { apiClient } from './base/client';
import { ApiError, UploadApiError } from './base/errors';
import { acquireBearerToken, msalInstance } from './auth';

const API_VERSION = import.meta.env.VITE_API_VERSION || 'v3';
const BASE_API_URL = import.meta.env.VITE_API_BASE_URL || window.location.origin;
const BASE_URL = `${BASE_API_URL}/api/${API_VERSION}`;

/**
 * Upload request interface
 */
export interface UploadRequest {
  files: File[];
  conversationId: string;
  deletedFiles?: string[];
}

/**
 * Upload response interface matching backend 202 Accepted response
 */
export interface UploadResponse {
  upload_session_id: string;
  conversation_id: string;
  status: 'processing';
}

/**
 * Upload token response from /upload-token endpoint
 */
interface UploadTokenResponse {
  conversation_id: string;
  upload_session_id: string;
  tokens: Record<string, { sas_url: string; blob_name: string }>;
  expires_at: string | null;
}

/**
 * Upload processing status response from the dedicated status endpoint
 */
export interface UploadStatusResponse {
  status: 'processing' | 'complete' | 'failed' | 'unknown';
}

/**
 * Fetch the current processing status for a conversation's file upload.
 */
export async function fetchUploadStatus(conversationId: string): Promise<UploadStatusResponse> {
  return apiClient.get<UploadStatusResponse>(`/upload_status?conversationID=${encodeURIComponent(conversationId)}`);
}

/**
 * Upload error interface for detailed error handling
 */
export interface UploadError {
  error: string;
  details?: any;
}

/**
 * Delete files request interface
 */
export interface DeleteFilesRequest {
  conversationId: string;
  filenames: string[];
}

/**
 * Detect backend feature-flag mismatch where DirectToBlob endpoints are disabled.
 */
function isDirectToBlobUnavailable(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

/**
 * Upload a single file directly to Azure Blob Storage using a SAS URL.
 */
async function uploadFileToBlob(file: File, sasUrl: string): Promise<void> {
  const response = await fetch(sasUrl, {
    method: 'PUT',
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type': file.type || 'application/octet-stream',
    },
    body: file,
  });

  if (!response.ok) {
    throw new Error(`Blob upload failed for "${file.name}": ${response.status} ${response.statusText}`);
  }
}

/**
 * Upload files using direct-to-blob flow:
 * 1. Request SAS tokens from backend
 * 2. Upload each file directly to Azure Blob Storage
 * 3. Notify backend to start processing
 */
async function uploadFilesNew(request: UploadRequest): Promise<UploadResponse> {
  try {
    console.log('Starting direct-to-blob upload for conversation:', request.conversationId);
    console.log('Files to upload:', request.files.map(f => ({ name: f.name, size: f.size })));

    // Step 1: Request SAS tokens from backend
    const tokenResponse = await apiClient.post<UploadTokenResponse>('/upload-token', {
      conversation_id: request.conversationId,
      files: request.files.map(f => f.name),
    });

    console.log('SAS tokens received for', Object.keys(tokenResponse.tokens).length, 'files');

    // Step 2: Upload each file directly to blob storage
    const uploadPromises = request.files.map(async (file) => {
      const tokenInfo = tokenResponse.tokens[file.name];
      if (!tokenInfo) {
        throw new Error(`No upload token received for file "${file.name}"`);
      }
      await uploadFileToBlob(file, tokenInfo.sas_url);
      console.log(`Uploaded "${file.name}" to blob storage`);
    });

    await Promise.all(uploadPromises);
    console.log('All files uploaded to blob storage');

    // Step 3: Notify backend to start processing
    const completeResponse = await apiClient.post<UploadResponse>('/upload-complete', {
      conversation_id: request.conversationId,
      upload_session_id: tokenResponse.upload_session_id,
      files: request.files.map(f => f.name),
      deleted_files: request.deletedFiles || [],
      allFileExtensions: request.files.map(file => {
        const extension = file.name.split('.').pop()?.toLowerCase() || '';
        return extension;
      }),
    });

    console.log('Upload complete notification sent:', completeResponse);
    return completeResponse;
  } catch (error) {
    console.error('File upload error:', error);

    if (error instanceof ApiError) {
      throw error;
    }

    throw new UploadApiError(
      error instanceof Error ? error.message : 'Failed to upload files',
      0,
      {
        totalFiles: request.files.length,
        failedFiles: request.files.map(f => f.name),
      }
    );
  }
}

/**
 * Upload files using configured path, with automatic fallback for flag mismatch.
 */
export async function uploadFiles(request: UploadRequest): Promise<UploadResponse> {
  try {
    return await uploadFilesNew(request);
  } catch (error) {
    if (isDirectToBlobUnavailable(error)) {
      console.warn('DirectToBlob upload unavailable (404). Falling back to legacy /upload endpoint.');
      return uploadFilesLegacy(request);
    }
    throw error;
  }
}

/**
 * Legacy multipart upload: POST files as FormData directly to /upload.
 * TODO(file-upload-v2): Remove after prod rollout.
 */
export async function uploadFilesLegacy(request: UploadRequest): Promise<UploadResponse> {
  try {
    console.log('Starting legacy multipart upload for conversation:', request.conversationId);

    const bearerToken = await acquireBearerToken(msalInstance);
    const formData = new FormData();
    request.files.forEach((file, index) => {
      formData.append(`file${index}`, file);
    });

    const allFileExtensions = request.files.map(file => {
      const extension = file.name.split('.').pop()?.toLowerCase() || '';
      return extension;
    });

    formData.append('query', JSON.stringify({
      conversation_id: request.conversationId,
      overrides: {},
      deleted_files: request.deletedFiles || [],
      allFileExtensions,
    }));

    const response = await fetch(`${BASE_URL}/upload`, {
      method: 'POST',
      headers: {
        Authorization: bearerToken,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new UploadApiError(
        errorData.error || 'Upload failed',
        response.status,
        { totalFiles: request.files.length, failedFiles: request.files.map(f => f.name) }
      );
    }

    const data = await response.json();
    return {
      upload_session_id: data.upload_session_id,
      conversation_id: data.conversation_id,
      status: 'processing',
    };
  } catch (error) {
    console.error('Legacy file upload error:', error);
    if (error instanceof ApiError) {
      throw error;
    }
    throw new UploadApiError(
      error instanceof Error ? error.message : 'Failed to upload files',
      0,
      { totalFiles: request.files.length, failedFiles: request.files.map(f => f.name) }
    );
  }
}

/**
 * Delete files from conversation using the upload-complete endpoint (new path)
 */
async function deleteFilesNew(request: DeleteFilesRequest): Promise<UploadResponse> {
  try {
    console.log('Starting file deletion for conversation:', request.conversationId);
    console.log('Files to delete:', request.filenames);

    const allFileExtensions = request.filenames.map(filename => {
      const extension = filename.split('.').pop()?.toLowerCase() || '';
      return extension;
    });

    const response = await apiClient.post<UploadResponse>('/upload-complete', {
      conversation_id: request.conversationId,
      upload_session_id: '',
      files: [],
      deleted_files: request.filenames,
      allFileExtensions,
    });

    console.log('File deletion successful:', response);
    return response;
  } catch (error) {
    console.error('File deletion error:', error);

    if (error instanceof ApiError) {
      throw error;
    }

    throw new UploadApiError(
      'Failed to delete files',
      0,
      {
        totalFiles: request.filenames.length,
        failedFiles: request.filenames,
      }
    );
  }
}

/**
 * Delete files from conversation using the legacy /upload endpoint (FormData).
 * TODO(file-upload-v2): Remove after prod rollout.
 */
async function deleteFilesLegacy(request: DeleteFilesRequest): Promise<UploadResponse> {
  try {
    console.log('Starting legacy file deletion for conversation:', request.conversationId);

    const bearerToken = await acquireBearerToken(msalInstance);
    const formData = new FormData();

    const allFileExtensions = request.filenames.map(filename => {
      const extension = filename.split('.').pop()?.toLowerCase() || '';
      return extension;
    });

    formData.append('query', JSON.stringify({
      conversation_id: request.conversationId,
      overrides: {},
      deleted_files: request.filenames,
      allFileExtensions,
    }));

    const response = await fetch(`${BASE_URL}/upload`, {
      method: 'POST',
      headers: {
        Authorization: bearerToken,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new UploadApiError(
        errorData.error || 'Deletion failed',
        response.status,
        { totalFiles: request.filenames.length, failedFiles: request.filenames }
      );
    }

    const data = await response.json();
    return {
      upload_session_id: data.upload_session_id || '',
      conversation_id: data.conversation_id,
      status: 'processing',
    };
  } catch (error) {
    console.error('Legacy file deletion error:', error);
    if (error instanceof ApiError) {
      throw error;
    }
    throw new UploadApiError(
      'Failed to delete files',
      0,
      { totalFiles: request.filenames.length, failedFiles: request.filenames }
    );
  }
}

/**
 * Delete files — routes to new or legacy implementation based on feature flag,
 * with fallback if DirectToBlob endpoints are unavailable.
 * TODO(file-upload-v2): Simplify to just deleteFilesNew after prod rollout.
 */
export async function deleteFiles(request: DeleteFilesRequest): Promise<UploadResponse> {
  try {
    return await deleteFilesNew(request);
  } catch (error) {
    if (isDirectToBlobUnavailable(error)) {
      console.warn('DirectToBlob delete unavailable (404). Falling back to legacy /upload endpoint.');
      return deleteFilesLegacy(request);
    }
    throw error;
  }
}

/**
 * TanStack Query configuration
 */

/**
 * Query keys factory for upload operations
 */
export const uploadQueryKeys = {
  all: ['uploads'] as const,
  uploads: () => [...uploadQueryKeys.all, 'upload'] as const,
  upload: (conversationId: string) => [...uploadQueryKeys.uploads(), conversationId] as const,
};

/**
 * TanStack Query mutation options for file uploads.
 * Routes to DirectToBlob or legacy multipart based on feature flag.
 * TODO(file-upload-v2): Remove legacy branch after prod rollout.
 */
export const uploadMutationOptions = (conversationId: string): MutationOptions<UploadResponse, UploadApiError, UploadRequest> => ({
  mutationKey: uploadQueryKeys.upload(conversationId),
  mutationFn: uploadFiles,
  retry: (failureCount, error) => {
    // Use the error's built-in retry logic
    if (error instanceof UploadApiError) {
      return error.isRetryable && failureCount < 2;
    }
    // Don't retry unknown errors
    return false;
  },
  retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
});
